import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useLayoutEffect, useRef, useEffect } from 'react';
import ButtonComponent from './ButtonComponent';
import '../styles/toggleButton.scss';
import { t } from 'i18next';
import { useOnResize } from '../helper/hooks/onResize';
import { toFixed0 } from '../helper/helper';
var ToggleButton = function (props) {
    var _a;
    var _b = props.initial, initial = _b === void 0 ? 'EN' : _b, onChange = props.onChange, _c = props.className, className = _c === void 0 ? '' : _c, _d = props.normalCSS, normalCSS = _d === void 0 ? '' : _d, _e = props.disabled, disabled = _e === void 0 ? false : _e, _f = props.options, options = _f === void 0 ? [
        {
            id: 'toggle1',
            ariaLabel: 'english',
            value: 'EN',
            title: 'text.langEN',
        },
        {
            id: 'toggle2',
            ariaLabel: 'spanish',
            value: 'ES',
            title: 'text.langES',
        },
    ] : _f, _g = props.textVisible, textVisible = _g === void 0 ? true : _g, _h = props.ariaLabel, ariaLabel = _h === void 0 ? 'language toggle' : _h, _j = props.isWhiteBoard, isWhiteBoard = _j === void 0 ? false : _j, _k = props.isParentRelative, isParentRelative = _k === void 0 ? false : _k, _l = props.alternateColor, alternateColor = _l === void 0 ? false : _l, _m = props.toogleTooltipDirection, toogleTooltipDirection = _m === void 0 ? 'bottom' : _m, _o = props.addButtonToToggle, addButtonToToggle = _o === void 0 ? false : _o, _p = props.toggleType, toggleType = _p === void 0 ? 'button' : _p;
    var isSwitch = toggleType === 'switch';
    var _q = useState(initial), value = _q[0], setValue = _q[1];
    var _r = useState({
        left: 0,
        top: 0,
    }), indicatorPos = _r[0], setIndicatorPos = _r[1];
    var _s = useState(false), toggleMouseOver = _s[0], setToggleMouseOver = _s[1];
    var _t = useState(false), btnMouseOver = _t[0], setBtnMouseOver = _t[1];
    var _u = useState({
        width: 0,
        height: 0,
    }), btnDimension = _u[0], setBtnDimension = _u[1];
    var _v = useState(false), transitionEnabled = _v[0], setTransitionEnabled = _v[1];
    var _w = useState(false), touchActive = _w[0], setTouchActive = _w[1];
    var _x = useState(null), activeBtnIndex = _x[0], setActiveBtnIndex = _x[1];
    var toggleRef = useRef(null);
    var optionRefs = useRef([]);
    useEffect(function () {
        setValue(initial);
    }, [initial]);
    var handleToggle = useCallback(function (index) {
        var _a;
        // Compute the next value from the current `value` and call onChange OUTSIDE
        // the setValue updater. Calling onChange (which setStates the parent) inside
        // the updater runs it during render → "Cannot update a component while
        // rendering" warning and unreliable updates.
        var newIndex = index !== undefined
            ? index
            : (options.findIndex(function (option) { return option.value === value; }) + 1) % options.length;
        var newLang = (_a = options[newIndex].value) !== null && _a !== void 0 ? _a : '';
        setValue(newLang);
        setTransitionEnabled(true);
        onChange === null || onChange === void 0 ? void 0 : onChange(newLang);
    }, [onChange, options, value]);
    var computeSizes = useCallback(function () {
        var _a;
        var toggleEl = toggleRef.current;
        var activeIndex = options.findIndex(function (option) { return option.value === value; });
        var activeBtnEl = (_a = optionRefs.current) === null || _a === void 0 ? void 0 : _a[activeIndex];
        if (!toggleEl || !activeBtnEl)
            return;
        // Measure active button
        var btnWidth = activeBtnEl.clientWidth;
        var btnHeight = activeBtnEl.clientHeight;
        // Get actual button position using offsetLeft/offsetTop (relative to parent)
        var left = activeBtnEl.offsetLeft;
        var top = activeBtnEl.offsetTop;
        // Only update state if values actually changed (prevents unnecessary re-renders)
        setBtnDimension(function (prev) {
            return prev.width === btnWidth && prev.height === btnHeight ? prev : { width: btnWidth, height: btnHeight };
        });
        setIndicatorPos(function (prev) {
            return prev.left === left && prev.top === top ? prev : { left: left, top: top };
        });
    }, [options.length, value]);
    useEffect(function () {
        computeSizes();
    }, [value, computeSizes]);
    // run before paint for synchronous update (no flicker)
    useLayoutEffect(function () {
        computeSizes();
    }, []);
    // keep responsive
    useOnResize(computeSizes);
    // Re-compute when button dimensions change (e.g. CSS variable font-size changes)
    // Also observe the pill itself: when the parent panel switches between layouts
    // (e.g. ArrayModel inline-grid vs. ColoredTiles flex-row), the button's
    // clientWidth/Height can stay identical while only offsetLeft/offsetTop change —
    // a button-only ResizeObserver wouldn't fire, leaving the indicator stale.
    // The pill's overall size always changes across those layouts, so observing
    // it catches the layout switch.
    useEffect(function () {
        var _a;
        var activeIndex = options.findIndex(function (option) { return option.value === value; });
        var activeBtnEl = (_a = optionRefs.current) === null || _a === void 0 ? void 0 : _a[activeIndex];
        var pillEl = toggleRef.current;
        if (!activeBtnEl && !pillEl)
            return;
        var ro = new ResizeObserver(function () {
            computeSizes();
        });
        if (activeBtnEl)
            ro.observe(activeBtnEl);
        if (pillEl)
            ro.observe(pillEl);
        return function () { return ro.disconnect(); };
    }, [value, options, computeSizes]);
    return (_jsxs("div", { className: "togglePill ".concat(className, " ").concat(disabled ? "togglePill_disable ".concat(normalCSS, "_disable") : '', " ").concat(alternateColor && value === options[1].value ? "togglePill_active ".concat(normalCSS, "_active") : ''), role: isSwitch ? 'switch' : 'group', tabIndex: (_a = props.tabIndex) !== null && _a !== void 0 ? _a : (isSwitch ? (disabled ? -1 : 0) : undefined), "aria-checked": isSwitch ? (alternateColor ? value === options[0].value : value === options[1].value) : undefined, "aria-label": isSwitch ? ariaLabel : undefined, "aria-hidden": disabled || props.ariaHidden ? true : undefined, "aria-disabled": disabled || undefined, ref: toggleRef, onClick: function (e) {
            if (e.target === toggleRef.current && !disabled)
                handleToggle();
        }, onKeyDown: isSwitch
            ? function (e) {
                if (disabled)
                    return;
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    handleToggle();
                }
            }
            : function (e) {
                // Focus-only swap between the two inner option buttons (EN/ES).
                // At the outer edge in the direction of the arrow, let the
                // event bubble so the parent roving toolbar can leave this
                // group. Same pattern as HalfAndHalfToggleButton.
                if (disabled)
                    return;
                var prevKeys = ['ArrowLeft', 'ArrowUp'];
                var nextKeys = ['ArrowRight', 'ArrowDown'];
                if (!prevKeys.includes(e.key) && !nextKeys.includes(e.key))
                    return;
                var buttons = optionRefs.current.filter(Boolean);
                if (buttons.length < 2)
                    return;
                var currentIdx = buttons.indexOf(document.activeElement);
                var step = prevKeys.includes(e.key) ? -1 : 1;
                var targetIdx = currentIdx + step;
                // Already at the edge in the arrow direction → bubble to parent.
                if (targetIdx < 0 || targetIdx >= buttons.length)
                    return;
                e.preventDefault();
                e.stopPropagation();
                buttons[targetIdx].focus();
            }, onMouseOver: function () {
            if (!disabled)
                setToggleMouseOver(true);
        }, onMouseOut: function () {
            if (!disabled)
                setToggleMouseOver(false);
        }, onTouchStart: function () {
            if (!disabled)
                setTouchActive(true);
        }, onTouchEnd: function () {
            if (!disabled)
                setTouchActive(false);
        }, onTouchCancel: function () {
            if (!disabled)
                setTouchActive(false);
        }, children: [_jsx("div", { className: "toggleIndicator ".concat(toggleMouseOver && (!btnMouseOver || !textVisible) ? 'toggleIndicator_hover' : ''), "aria-hidden": "true", style: {
                    left: toFixed0(indicatorPos.left),
                    top: toFixed0(indicatorPos.top),
                    width: toFixed0(btnDimension.width),
                    height: toFixed0(btnDimension.height),
                    transition: transitionEnabled ? 'left 0.4s, top 0.4s' : 'none',
                }, onTransitionStart: function () {
                    setTransitionEnabled(true);
                }, onTransitionEnd: function () {
                    setTransitionEnabled(false);
                } }), options.map(function (_text, _ind) {
                var _a, _b;
                return (_jsx(ButtonComponent, { text: textVisible ? t((_a = (_text.valueVisible || _text.value)) !== null && _a !== void 0 ? _a : '') : '', defaultCSS: "pillBtn".concat(value === _text.value ? ' pillActive' : '').concat(activeBtnIndex === _ind ? ' pillTouchActive' : ''), normalCSS: "toggleBtn", clickHandler: function (e) {
                        e.stopPropagation();
                        handleToggle(addButtonToToggle ? _ind : undefined);
                        setActiveBtnIndex(null);
                    }, ariaLabel: _text.ariaLabel, ariaHidden: isSwitch || props.tabIndex === -1, ariaPressed: !isSwitch ? value === _text.value : undefined, imgSrc: _text === null || _text === void 0 ? void 0 : _text.imgSrc, showToolTip: (_text === null || _text === void 0 ? void 0 : _text.title) !== undefined, direction: toogleTooltipDirection, isParentRelative: isParentRelative, title: _text === null || _text === void 0 ? void 0 : _text.title, isWhiteBoard: isWhiteBoard, reference: function (el) {
                        optionRefs.current[_ind] = el;
                    }, rollOverDisabled: !textVisible, disabled: disabled, tabIndex: (_b = props.tabIndex) !== null && _b !== void 0 ? _b : (value === _text.value ? undefined : -1), onMouseOver: function () {
                        if (!disabled)
                            setBtnMouseOver(true);
                    }, onMouseOut: function () {
                        if (!disabled)
                            setBtnMouseOver(false);
                    }, onTouchStart: function () {
                        if (!disabled) {
                            setActiveBtnIndex(_ind);
                            setTouchActive(true);
                        }
                    }, onTouchEnd: function () {
                        if (!disabled) {
                            setActiveBtnIndex(null);
                            setTouchActive(false);
                        }
                    }, onTouchCancel: function () {
                        if (!disabled) {
                            setActiveBtnIndex(null);
                            setTouchActive(false);
                        }
                    } }, "btn_".concat(_ind)));
            })] }));
};
export default React.memo(ToggleButton);
